import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Checkbox, FormControlLabel, Button } from '@mui/material';
import { AccountCircle, Business, Upload, LocationOn } from '@mui/icons-material';
import OnboardingLayout from '../../components/host/OnboardingLayout';
import { 
  FormField, 
  SelectField, 
  UploadZone, 
  NavigationButtons, 
  InfoCard 
} from '../../components/host/OnboardingComponents';
import GooglePlacesAutocomplete from '../../components/common/GooglePlacesAutocomplete';
import GoogleMapDisplay from '../../components/host/GoogleMapDisplay';
import GoogleMapsService from '../../services/googleMapsService';

interface VerificationData {
  // Contact Information
  hostName: string;
  emailAddress: string;
  phoneNumber: string;
  businessName: string;
  businessType: string;
  
  // Documents
  governmentId: File | null;
  businessLicense: File | null;
  
  // Property Location
  address: string;
  latitude: number | null;
  longitude: number | null;
  neighborhoodDescription: string;
  
  // Agreement
  agreesToTerms: boolean;
}

const businessTypes = [
  { value: 'individual', label: 'Individual Property Owner' },
  { value: 'llc', label: 'LLC' },
  { value: 'corporation', label: 'Corporation' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'property_management', label: 'Property Management Company' },
  { value: 'hotel', label: 'Hotel/Hospitality' },
  { value: 'other', label: 'Other' },
];

const HostOnboardingStep2 = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<VerificationData>({
    hostName: '',
    emailAddress: '',
    phoneNumber: '',
    businessName: '',
    businessType: '',
    governmentId: null,
    businessLicense: null,
    address: '',
    latitude: null,
    longitude: null,
    neighborhoodDescription: '',
    agreesToTerms: false,
  });
  const handleNext = () => {
    if (canProceed()) {
      // Store the form data
      localStorage.setItem('hostOnboardingStep2', JSON.stringify({
        ...formData,
        governmentId: formData.governmentId?.name || null,
        businessLicense: formData.businessLicense?.name || null,
      }));
      navigate('/host/onboarding/step-3');
    }
  };

  const handlePrevious = () => {
    navigate('/host/onboarding/step-1');
  };

  const canProceed = () => {
    const requiredFields = [
      formData.hostName,
      formData.emailAddress,
      formData.phoneNumber,
      formData.businessName,
      formData.businessType,
      formData.address,
      formData.neighborhoodDescription,
    ];
    
    return requiredFields.every(field => field.trim() !== '') &&
           formData.governmentId !== null &&
           formData.businessLicense !== null &&
           formData.agreesToTerms;
  };

  const updateFormData = (field: keyof VerificationData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePlaceSelect = (place: { address: string; latitude: number; longitude: number; formatted_address: string }) => {
    // Update form data with place coordinates - this will trigger map update
    setFormData(prev => ({
      ...prev,
      address: place.formatted_address,
      latitude: place.latitude,
      longitude: place.longitude
    }));
  };

  const handleMapLocationChange = async (lat: number, lng: number) => {
    // Update coordinates when marker is dragged or map is clicked
    setFormData(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng
    }));

    // Reverse geocode to update the address field when map is dragged
    try {
      const googleMapsService = GoogleMapsService.getInstance();
      await googleMapsService.loadGoogleMaps();
      
      if (typeof google !== 'undefined' && google.maps && (google.maps as any).Geocoder) {
        const geocoder = new (google.maps as any).Geocoder();
        
        geocoder.geocode({ location: { lat, lng } }, (results: any, status: string) => {
          if (status === 'OK' && results && results[0]) {
            // Update the address field with the reverse geocoded address
            setFormData(prev => ({
              ...prev,
              address: results[0].formatted_address || prev.address
            }));
          } else {
            console.warn('Reverse geocoding failed:', status);
          }
        });
      }
    } catch (error) {
      console.error('Error reverse geocoding:', error);
      // Keep the coordinates even if reverse geocoding fails
    }
  };

  // Geocode address when field is blurred (user leaves the field) to update address, lat and long
  const handleAddressBlur = async () => {
    // Geocode if address exists to get formatted address, latitude, and longitude
    if (formData.address && formData.address.trim().length > 0) { 
      try {
        const googleMapsService = GoogleMapsService.getInstance();
        await googleMapsService.loadGoogleMaps();
        
        if (typeof google !== 'undefined' && google.maps && (google.maps as any).Geocoder) {
          const geocoder = new (google.maps as any).Geocoder();
          
          // Geocode the address to get formatted address, latitude, and longitude
          geocoder.geocode({ address: formData.address }, (results: any, status: string) => {
            if (status === 'OK' && results && results[0] && results[0].geometry && results[0].geometry.location) {
              const location = results[0].geometry.location;
              const newLat = typeof location.lat === 'function' ? location.lat() : location.lat;
              const newLng = typeof location.lng === 'function' ? location.lng() : location.lng;
              const formattedAddress = results[0].formatted_address || formData.address;
              
              // Update formData with formatted address, latitude, and longitude
              setFormData(prev => ({
                ...prev,
                address: formattedAddress,
                latitude: newLat,
                longitude: newLng
              }));
            } else {
              console.warn('Geocoding failed on blur:', status);
            }
          });
        }
      } catch (error) {
        console.error('Error geocoding address on blur:', error);
      }
    }
  };

  // Geocode address to get longitude and latitude and save in useState
  useEffect(() => {
    const geocodeAddress = async () => {
      // Only geocode if address exists and has content
      if (formData.address && formData.address.trim().length > 0) {
        try {
          const googleMapsService = GoogleMapsService.getInstance();
          await googleMapsService.loadGoogleMaps();
          
          if (typeof google !== 'undefined' && google.maps && (google.maps as any).Geocoder) {
            const geocoder = new (google.maps as any).Geocoder();
            
            geocoder.geocode({ address: formData.address }, (results: any, status: string) => {
              if (status === 'OK' && results && results[0] && results[0].geometry && results[0].geometry.location) {
                const location = results[0].geometry.location;
                const newLat = typeof location.lat === 'function' ? location.lat() : location.lat;
                const newLng = typeof location.lng === 'function' ? location.lng() : location.lng;
                
                // Always save coordinates to useState when geocoding succeeds
                setFormData(prev => {
                  // Check if coordinates need to be updated (missing or significantly different)
                  const shouldUpdate = prev.latitude === null || 
                                      prev.longitude === null || 
                                      Math.abs((prev.latitude || 0) - newLat) > 0.0001 || 
                                      Math.abs((prev.longitude || 0) - newLng) > 0.0001;
                  
                  if (shouldUpdate) {
                    return {
                      ...prev,
                      latitude: newLat,
                      longitude: newLng,
                      // Update address with formatted address from geocoding if available
                      address: results[0].formatted_address || prev.address
                    };
                  }
                  return prev;
                });
              } else {
                // Log geocoding errors for debugging
                console.warn('Geocoding failed or returned no results:', status);
              }
            });
          }
        } catch (error) {
          console.error('Error geocoding address:', error);
          // Silently fail - user can still proceed with just address
        }
      }
    };

    // Debounce geocoding to avoid too many API calls
    const timeoutId = setTimeout(() => {
      geocodeAddress();
    }, 800); // Wait 800ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [formData.address]);

  return (
    <OnboardingLayout
      currentStep={2}
      totalSteps={4}
      stepTitle="Verification"
      stepSubtitle="Provide your information for account verification"
    >
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Contact Information */}
        <InfoCard title="Contact Information" icon={<AccountCircle />}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormField
                label="Host Name"
                value={formData.hostName}
                onChange={(value) => updateFormData('hostName', value)}
                placeholder="Add Name Here"
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormField
                label="Email Address"
                value={formData.emailAddress}
                onChange={(value) => updateFormData('emailAddress', value)}
                placeholder="Add Email Address Here"
                type="email"
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormField
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={(value) => updateFormData('phoneNumber', value)}
                placeholder="(+1) 702-334-567"
                type="tel"
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormField
                label="Business Name"
                value={formData.businessName}
                onChange={(value) => updateFormData('businessName', value)}
                placeholder="Add Business Name"
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <SelectField
                label="Business Type"
                value={formData.businessType}
                onChange={(value) => updateFormData('businessType', value)}
                options={businessTypes}
                required
              />
            </Grid>
          </Grid>
        </InfoCard>

        {/* Document Verification */}
        <InfoCard title="Document Verification" icon={<Upload />}>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" sx={{ color: '#fff', mb: 1, fontWeight: 600 }}>
                Government ID
              </Typography>
              <Typography variant="body2" sx={{ color: '#b0b8c1', mb: 2 }}>
                Upload A Clear Photo Of Your Government-Issued ID
              </Typography>
              <UploadZone
                title="Upload ID"
                subtitle="JPG, PNG or PDF (Max 5MB)"
                onUpload={(file) => updateFormData('governmentId', file)}
                acceptedTypes=".jpg,.jpeg,.png,.pdf"
                icon={<Upload />}
              />
              {formData.governmentId && (
                <Typography variant="body2" sx={{ color: '#4ade80', mt: 2 }}>
                  ✓ {formData.governmentId.name}
                </Typography>
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" sx={{ color: '#fff', mb: 1, fontWeight: 600 }}>
                Business License
              </Typography>
              <Typography variant="body2" sx={{ color: '#b0b8c1', mb: 2 }}>
                Upload Your Current Business License
              </Typography>
              <UploadZone
                title="Upload License"
                subtitle="JPG, PNG or PDF (Max 5MB)"
                onUpload={(file) => updateFormData('businessLicense', file)}
                acceptedTypes=".jpg,.jpeg,.png,.pdf"
                icon={<Business />}
              />
              {formData.businessLicense && (
                <Typography variant="body2" sx={{ color: '#4ade80', mt: 2 }}>
                  ✓ {formData.businessLicense.name}
                </Typography>
              )}
            </Grid>
          </Grid>
        </InfoCard>

        {/* Property Location */}
        <InfoCard title="Property Location" icon={<LocationOn />}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ color: '#fff', mb: 1, fontWeight: 600 }}>
              Address
            </Typography>
            <GooglePlacesAutocomplete
              // label="Property Address"
              placeholder="Start typing your address or enter manually..."
              value={formData.address}
              onChange={(value) => updateFormData('address', value)}
              onPlaceSelect={handlePlaceSelect}
              onBlur={handleAddressBlur}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#1a202c',
                  '& fieldset': {
                    borderColor: '#4a5568',
                  },
                  '&:hover fieldset': {
                    borderColor: '#377dff',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#377dff',
                    borderWidth: '2px',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#b0b8c1',
                  '&.Mui-focused': {
                    color: '#377dff',
                  },
                },
              }}
            />
            <Typography variant="caption" sx={{ color: '#6b7280', mt: 1, display: 'block' }}>
              {formData.address ? 'Address entered. You can proceed with manual entry.' : 'Type your address above. Autocomplete suggestions will appear if available, or you can enter manually.'}
            </Typography>
          </Box>
          
          {/* Google Map */}
          <Box sx={{ mb: 3 }}>
            {formData.latitude && formData.longitude ? (
              <GoogleMapDisplay
                key={`${formData.latitude}-${formData.longitude}`}
                latitude={formData.latitude}
                longitude={formData.longitude}
                address={formData.address}
                height={250}
                zoom={15}
                onLocationChange={handleMapLocationChange}
                draggable={true}
              />
            ) : formData.address ? (
              <Box
                sx={{
                  height: 250,
                  backgroundColor: '#1a202c',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #4a5568',
                  p: 3,
                }}
              >
                <LocationOn sx={{ color: '#6b7280', fontSize: 48, mb: 2 }} />
                <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center' }}>
                  Address: {formData.address}
                </Typography>
                <Typography variant="caption" sx={{ color: '#6b7280', textAlign: 'center', mt: 1 }}>
                  Map preview unavailable. You can still proceed with the address entered.
                </Typography>
              </Box>
            ) : (
              <GoogleMapDisplay
                latitude={40.7128}
                longitude={-74.0060}
                address=""
                height={250}
                zoom={2}
                onLocationChange={handleMapLocationChange}
                draggable={true}
              />
            )}
          </Box>

          <Typography variant="h6" sx={{ color: '#fff', mb: 1, fontWeight: 600 }}>
            Neighborhood Description
          </Typography>
          <FormField
            label=""
            value={formData.neighborhoodDescription}
            onChange={(value) => updateFormData('neighborhoodDescription', value)}
            placeholder="Describe The Local Attractions And Neighborhood Highlights..."
            multiline
            rows={4}
            required
          />
        </InfoCard>

        {/* Terms Agreement */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.agreesToTerms}
                onChange={(e) => updateFormData('agreesToTerms', e.target.checked)}
                sx={{
                  color: '#4a5568',
                  '&.Mui-checked': { color: '#377dff' }
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                I Agree To The Terms And Conditions And Verify That All Information Provided Is Accurate.
              </Typography>
            }
          />
        </Box>

        {/* Navigation */}
        <NavigationButtons
          onNext={handleNext}
          onPrevious={handlePrevious}
          nextLabel="Submit For Verification"
          canProceed={canProceed()}
        />
        
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
            Your Application Will Be Reviewed Within 1-2 Business Days
          </Typography>
        </Box>
      </Box>
    </OnboardingLayout>
  );
};

export default HostOnboardingStep2;
